/**
 * Translation catalog. Each top-level key is a namespace; nested keys map to strings.
 *
 * Supported interpolation: t("common.hello", { name: "Sarah" })  →  "Hello, Sarah"
 * Supported pluralisation: t("groups.count", { count: 2 })        →  "2 groups"
 *   - Pass a `count` key and use "_one" / "_other" suffixes on the string key.
 *
 * Adding a new language: copy the `en` block, translate the values, and register
 * the locale code in LOCALES at the bottom.
 */
export const translations = {
  en: {
    common: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      remove: "Remove",
      add: "Add",
      close: "Close",
      done: "Done",
      loading: "Loading...",
      search: "Search",
      signOut: "Sign Out",
      name: "Name",
      email: "Email",
    },
    help: {
      title: "Need help?",
      description: "Questions, suggestions, or trouble with your account? Send us a note and a real person will get back to you.",
      replyTime: "We usually reply within a day.",
      copy: "Copy",
      copied: "Copied!",
    },
    churches: {
      title: "Churches",
      subtitle: "Browse every church and the prayer groups that meet within it.",
      manageLink: "Manage churches",
      emptyTitle: "No churches yet",
      emptyHint: "An administrator will add churches soon.",
      noGroupsYet: "No groups yet at this church.",
      groups_one: "{{count}} group",
      groups_other: "{{count}} groups",
      members_one: "{{count}} member",
      members_other: "{{count}} members",
      managedBy: "Managed by",
      churchAdminBadge: "Church Admin",
      adminBadge: "Admin",
    },
    admin: {
      panelTitle: "Admin Panel",
      manageMyChurch: "Manage My Church",
      churchManagement: "Church Management",
      yourChurches: "Your Churches",
      addChurch: "Add Church",
      manageAdmins: "Manage admins",
      currentAdmins_one: "Current admins ({{count}})",
      currentAdmins_other: "Current admins ({{count}})",
      noAdminsYet: "No admins yet — add one from the list below.",
      addNewAdmin: "Add a new admin",
      searchUsers: "Search by name or email...",
      noUsersMatch: "No users match your search.",
      startTyping: "Start typing to find a user...",
    },
    contact: {
      weAreHereToHelp: "We're here to help",
      contactUs: "Contact us",
      contactBlurb: "Have a question, suggestion, or need help with your account? Reach out and a real person will get back to you.",
      mailtoHint: "Click the address to open your email, or copy it to paste elsewhere.",
    },
  },
  // ───────────────────────────────────────────────────────────
  // Spanish stub — values are placeholders. Fill in as needed.
  // ───────────────────────────────────────────────────────────
  es: {
    common: {
      save: "Guardar",
      cancel: "Cancelar",
      delete: "Eliminar",
      remove: "Quitar",
      add: "Añadir",
      close: "Cerrar",
      done: "Hecho",
      loading: "Cargando...",
      search: "Buscar",
      signOut: "Cerrar sesión",
      name: "Nombre",
      email: "Correo",
    },
    help: {
      title: "¿Necesitas ayuda?",
      description: "¿Preguntas, sugerencias o problemas con tu cuenta? Escríbenos y una persona real te responderá.",
      replyTime: "Normalmente respondemos en un día.",
      copy: "Copiar",
      copied: "¡Copiado!",
    },
    churches: {
      title: "Iglesias",
      subtitle: "Explora cada iglesia y los grupos de oración que se reúnen en ella.",
      manageLink: "Administrar iglesias",
      emptyTitle: "Aún no hay iglesias",
      emptyHint: "Un administrador agregará iglesias pronto.",
      noGroupsYet: "Aún no hay grupos en esta iglesia.",
      groups_one: "{{count}} grupo",
      groups_other: "{{count}} grupos",
      members_one: "{{count}} miembro",
      members_other: "{{count}} miembros",
      managedBy: "Administrado por",
      churchAdminBadge: "Admin de Iglesia",
      adminBadge: "Admin",
    },
    admin: {
      panelTitle: "Panel de Administrador",
      manageMyChurch: "Administrar mi iglesia",
      churchManagement: "Gestión de Iglesias",
      yourChurches: "Tus iglesias",
      addChurch: "Añadir iglesia",
      manageAdmins: "Administrar admins",
      currentAdmins_one: "Admin actual ({{count}})",
      currentAdmins_other: "Admins actuales ({{count}})",
      noAdminsYet: "Aún no hay admins — agrega uno de la lista de abajo.",
      addNewAdmin: "Agregar un nuevo admin",
      searchUsers: "Buscar por nombre o correo...",
      noUsersMatch: "Ningún usuario coincide con tu búsqueda.",
      startTyping: "Escribe para encontrar un usuario...",
    },
    contact: {
      weAreHereToHelp: "Estamos aquí para ayudar",
      contactUs: "Contáctanos",
      contactBlurb: "¿Tienes alguna pregunta, sugerencia o necesitas ayuda con tu cuenta? Comunícate y una persona real te responderá.",
      mailtoHint: "Haz clic en la dirección para abrir tu correo, o cópiala para pegar en otro lugar.",
    },
  },
};

export const LOCALES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

export const DEFAULT_LOCALE = "en";
